import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { trackPurchase } from "../lib/gtag";

const POLL_INTERVAL = 2500;
const MAX_POLLS = 8;

export default function PaymentResultPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled") === "1";
  const [status, setStatus] = useState({ state: "loading", payment_status: null, amount: null, kind: null });

  const poll = useCallback(async (attempt) => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus({ state: "paid", payment_status: "paid", amount: data.amount, kind: data.kind });
        // Google Ads conversion: purchase (deduped by session_id)
        trackPurchase({
          sessionId,
          value: typeof data.amount === "number" ? data.amount : Number(data.amount),
          currency: (data.currency || "EUR").toUpperCase(),
          kind: data.kind,
        });
        return;
      }
      if (data.status === "expired") {
        setStatus({ state: "expired", payment_status: data.payment_status });
        return;
      }
      if (attempt < MAX_POLLS) {
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } else {
        setStatus({ state: "timeout", payment_status: data.payment_status });
      }
    } catch (e) {
      setStatus({ state: "error", payment_status: null });
    }
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) {
      setStatus({ state: "cancelled" });
      return;
    }
    if (sessionId) poll(0);
  }, [sessionId, cancelled, poll]);

  const backHref = status.kind === "tournament_fee" || status.kind === "tournament_meal" ? "/tornei" : "/amministrazione";

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <Card className="p-10 max-w-md w-full text-center" data-testid="payment-result-card">
        {status.state === "loading" && (
          <>
            <Loader2 size={48} className="animate-spin mx-auto text-sky-500 mb-3" />
            <h2 className="font-heading text-2xl font-bold">Verifica pagamento...</h2>
            <p className="text-slate-500 mt-2">Stiamo confermando il pagamento con Stripe.</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="font-heading text-3xl font-bold">Pagamento completato</h2>
            {status.amount && <p className="text-3xl font-bold mt-3 text-emerald-600">€ {Number(status.amount).toFixed(2)}</p>}
            <p className="text-slate-500 mt-2 text-sm">Grazie! Riceverai a breve la ricevuta da Stripe.</p>
          </>
        )}
        {status.state === "cancelled" && (
          <>
            <AlertTriangle size={48} className="mx-auto text-amber-500 mb-3" />
            <h2 className="font-heading text-2xl font-bold">Pagamento annullato</h2>
            <p className="text-slate-500 mt-2">Puoi riprovare quando vuoi.</p>
          </>
        )}
        {status.state === "expired" && (
          <>
            <AlertTriangle size={48} className="mx-auto text-amber-500 mb-3" />
            <h2 className="font-heading text-2xl font-bold">Sessione scaduta</h2>
          </>
        )}
        {(status.state === "timeout" || status.state === "error") && (
          <>
            <AlertTriangle size={48} className="mx-auto text-amber-500 mb-3" />
            <h2 className="font-heading text-2xl font-bold">Stato in attesa</h2>
            <p className="text-slate-500 mt-2 text-sm">Ricarica la pagina tra qualche istante per vedere il pagamento aggiornato.</p>
          </>
        )}
        <Button onClick={() => nav(backHref)} className="mt-6 bg-sky-500 hover:bg-sky-600" data-testid="back-from-payment">
          Torna alla pagina
        </Button>
      </Card>
    </div>
  );
}
